import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  CreateUnitPricingRequest,
  ModelUnitPricingRecord,
  UnitUsageUnit,
} from '@nadoutong/contracts';
import {
  KNOWN_UNIT_USAGE_UNITS,
  MAX_UNIT_PRICING_RATES,
  UNIT_VARIANT_KEY_RE,
  isUnitMeteredProfileKind,
  resolveStoredMeteringSpec,
  unitMeteringSpecFrom,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCreateUnitPricing,
  useModelProtocols,
  useModelUnitPricing,
} from '../api/useModels.ts';
import {
  EffectiveFromPicker,
  immediateEffectiveFromIso,
  type EffectiveFromMode,
} from './EffectiveFromPicker.tsx';
import {
  formatLocalTimestamp,
  formatUsdMicros,
  localDateTimeInputValueToIso,
  parseTimestampMs,
} from './formatters.ts';
import { ratedAnchorConflictLatestIso } from './pricingErrors.ts';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import {
  MeteringShapeSummary,
  VariantFieldInputs,
  VariantKeyPreview,
  allVariantValuesEmpty,
  composeVariantKey,
  parseVariantKey,
  type VariantFieldValues,
} from './VariantKeyFields.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  FormField,
  Input,
  Select,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * Unit pricing region (#1669, S8): the operator face over
 * `model_unit_pricing` — generations grouped by `effective_from`, plus a
 * "new generation" form whose client-side validation uses the SAME
 * contracts constants the platform write face refuses with
 * (`MAX_UNIT_PRICING_RATES`, `UNIT_VARIANT_KEY_RE`), so the two faces
 * cannot drift and an operator meets a readable message instead of a
 * proxied 400.
 *
 * Since the 2026-08-17 reorder this renders as the UNIT SECTION of the one
 * pricing box (`ModelDetail`'s `PricingCard`), no longer a standalone Panel
 * — the profile registrations above the box decide which lanes it grows.
 *
 * Visibility: the section renders only when a registered protocol carries a
 * unit-metered metering profile; any other model renders NOTHING here
 * (owner decision, 2026-08-16). This retired #1669's collapsed disclosure,
 * and with it the console path that could author a pricing generation
 * BEFORE the unit profile was registered — an accepted trade-off: the
 * configuration order is register the unit profile first, then price it.
 * (The platform write face still accepts either order; only this console
 * entry point changed.)
 */

const UNIT_LABEL_KEYS: Readonly<Record<UnitUsageUnit, I18nKey>> = {
  call: 'model_detail.unit_pricing.unit.call',
  second: 'model_detail.unit_pricing.unit.second',
  character: 'model_detail.unit_pricing.unit.character',
  image: 'model_detail.unit_pricing.unit.image',
};

function isUnitUsageUnit(value: unknown): value is UnitUsageUnit {
  return KNOWN_UNIT_USAGE_UNITS.some((unit) => unit === value);
}

interface RateRowDraft {
  readonly unit: UnitUsageUnit;
  readonly variantKey: string;
  /**
   * Per-template-field values (#1883): when the governing unit spec declares
   * a `variant_key_template`, the key is DERIVED from these through the
   * contracts' own composition rule instead of being hand-typed. All fields
   * empty means the default (`''`) row, exactly as an empty key did.
   */
  readonly fields: VariantFieldValues;
  readonly rateMicros: string;
}

const EMPTY_ROW: RateRowDraft = { unit: 'call', variantKey: '', fields: {}, rateMicros: '' };

/**
 * The tier axis the governing unit spec declares, when one is readable:
 * stored spec first, the profile's built-in otherwise — the same resolution
 * (`unitMeteringSpecFrom`) the gateway prices by. `undefined` keeps the
 * free-text variant key exactly as it was, which is every unit SKU whose
 * spec declares no template.
 */
function unitTierAxisFrom(
  protocols: readonly { metering_profile: unknown; metering_spec?: unknown }[],
): { readonly template: string; readonly profile: string } | undefined {
  for (const entry of protocols) {
    if (!isUnitMeteredProfileKind(entry.metering_profile)) continue;
    const spec = unitMeteringSpecFrom(
      entry.metering_profile,
      resolveStoredMeteringSpec(entry.metering_spec),
    );
    if (spec !== null && spec.variant_key_template !== undefined) {
      return { template: spec.variant_key_template, profile: entry.metering_profile };
    }
  }
  return undefined;
}

/**
 * The unit the model's unit-lane registrations SELL IN, when it is knowable
 * and single — the same resolution the gateway prices by
 * (`unitMeteringSpecFrom`: stored spec first, the profile's built-in
 * otherwise), so the lock can never disagree with what a request is billed
 * as. The unit is not an operator choice on such a model: the governing
 * spec's `unit` field is what rating reads, and a dropdown offering
 * `character` on a per-image SKU is offering a row rating will never match.
 *
 * `undefined` — no resolvable spec (a unit registration whose SKU is
 * fail-closed `MODEL_BILLING_UNAVAILABLE` anyway), or two registrations
 * resolving to DIFFERENT units — keeps the free dropdown: where the answer
 * is unknowable or plural, locking would be inventing it.
 */
function lockedUnitFrom(
  protocols: readonly { metering_profile: unknown; metering_spec?: unknown }[],
): UnitUsageUnit | undefined {
  const units = new Set<UnitUsageUnit>();
  for (const entry of protocols) {
    if (!isUnitMeteredProfileKind(entry.metering_profile)) continue;
    const spec = unitMeteringSpecFrom(
      entry.metering_profile,
      resolveStoredMeteringSpec(entry.metering_spec),
    );
    if (spec !== null) units.add(spec.unit);
  }
  const resolved = [...units];
  return resolved.length === 1 ? resolved[0] : undefined;
}

/**
 * Next local midnight as a `datetime-local` value. The default matches the
 * adjacent token pricing form's semantics (a generation that takes effect
 * at a day boundary), while staying editable down to the minute for
 * operators who need an intra-day cutover.
 */
function nextLocalMidnightInputValue(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
}

type GenerationStatus = 'current' | 'scheduled' | 'archived';

interface Generation {
  readonly effectiveFrom: string;
  readonly rows: readonly ModelUnitPricingRecord[];
}

/** Group the newest-first flat list into generations by `effective_from`. */
function groupGenerations(records: readonly ModelUnitPricingRecord[]): Generation[] {
  const generations: Generation[] = [];
  for (const record of records) {
    const last = generations[generations.length - 1];
    if (last !== undefined && last.effectiveFrom === record.effective_from) {
      generations[generations.length - 1] = {
        effectiveFrom: last.effectiveFrom,
        rows: [...last.rows, record],
      };
    } else {
      generations.push({ effectiveFrom: record.effective_from, rows: [record] });
    }
  }
  return generations;
}

interface Props {
  readonly vendor: string;
  readonly model: string;
}

export function UnitPricingCard({ vendor, model }: Props): JSX.Element | null {
  const protocols = useModelProtocols(vendor, model);

  const hasUnitProfile = useMemo(
    () =>
      (protocols.data ?? []).some((p) => isUnitMeteredProfileKind(p.metering_profile)),
    [protocols.data],
  );
  const tierAxis = useMemo(() => unitTierAxisFrom(protocols.data ?? []), [protocols.data]);
  const lockedUnit = useMemo(() => lockedUnitFrom(protocols.data ?? []), [protocols.data]);

  // No unit-lane profile (or protocols still loading, which resolves the
  // same way so the card never flashes in late): nothing renders, and the
  // body's unit-pricing query is never mounted — a token model's page
  // issues zero /unit-pricing requests. See the header comment for the
  // retired disclosure and the ordering trade-off this accepts.
  if (!hasUnitProfile) return null;

  return (
    <UnitPricingCardBody
      vendor={vendor}
      model={model}
      tierAxis={tierAxis}
      lockedUnit={lockedUnit}
    />
  );
}

interface BodyProps extends Props {
  readonly tierAxis: { readonly template: string; readonly profile: string } | undefined;
  readonly lockedUnit: UnitUsageUnit | undefined;
}

function UnitPricingCardBody({ vendor, model, tierAxis, lockedUnit }: BodyProps): JSX.Element {
  const t = useT();
  const pricing = useModelUnitPricing(vendor, model);

  const generations = useMemo(
    () => groupGenerations(pricing.data ?? []),
    [pricing.data],
  );
  const currentEffective = useMemo<string | null>(() => {
    const now = Date.now();
    for (const generation of generations) {
      const ts = parseTimestampMs(generation.effectiveFrom);
      if (Number.isFinite(ts) && ts <= now) return generation.effectiveFrom;
    }
    return null;
  }, [generations]);
  const currentGeneration = useMemo(
    () => generations.find((generation) => generation.effectiveFrom === currentEffective),
    [currentEffective, generations],
  );

  function statusOf(generation: Generation): GenerationStatus {
    if (generation.effectiveFrom === currentEffective) return 'current';
    const ts = parseTimestampMs(generation.effectiveFrom);
    return Number.isFinite(ts) && ts > Date.now() ? 'scheduled' : 'archived';
  }

  return (
    <section
      className="flex flex-col gap-4"
      data-testid="model-unit-pricing-card"
    >
      <div className="flex items-center gap-3">
        <span
          className="text-body font-medium text-paper"
          data-testid="model-pricing-lane-unit"
        >
          {t('model_detail.unit_pricing.title')}
        </span>
        {pricing.isPending ? (
          <span className="text-label text-paper-low">
            {t('model_detail.unit_pricing.loading')}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-4">
        {pricing.isError ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="model-unit-pricing-load-error"
          >
            {pricing.error instanceof OpsApiError
              ? `${pricing.error.code}: ${pricing.error.message}`
              : t('model_detail.unit_pricing.error.load')}
          </p>
        ) : null}
        {pricing.isSuccess && generations.length === 0 ? (
          <p className="m-0 text-body text-paper-low" data-testid="model-unit-pricing-empty">
            {t('model_detail.unit_pricing.empty')}
          </p>
        ) : null}
        {generations.map((generation) => (
          <GenerationTable
            key={generation.effectiveFrom}
            generation={generation}
            status={statusOf(generation)}
          />
        ))}
        <NewGenerationForm
          vendor={vendor}
          model={model}
          tierAxis={tierAxis}
          lockedUnit={lockedUnit}
          currentEffective={currentEffective}
          currentRows={currentGeneration?.rows}
        />
      </div>
    </section>
  );
}

// ─── generation table ────────────────────────────────────────────────────

interface GenerationTableProps {
  readonly generation: Generation;
  readonly status: GenerationStatus;
}

function GenerationTable({ generation, status }: GenerationTableProps): JSX.Element {
  const t = useT();

  function humanRate(record: ModelUnitPricingRecord): string {
    return `${formatUsdMicros(record.rate_usd_micros_per_unit, false)} / ${t(UNIT_LABEL_KEYS[record.unit])}`;
  }

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`model-unit-pricing-gen-${generation.effectiveFrom}`}
    >
      <div className="flex items-center gap-3">
        <span className="font-mono text-dense text-paper-mid">
          {formatLocalTimestamp(generation.effectiveFrom, { seconds: true })}
        </span>
        {status === 'current' ? (
          <Chip tone="signal" testId={`model-unit-pricing-current-${generation.effectiveFrom}`}>
            {t('model_detail.unit_pricing.generation.current')}
          </Chip>
        ) : null}
        {status === 'scheduled' ? (
          <Chip
            tone="neutral"
            lamp={false}
            testId={`model-unit-pricing-scheduled-${generation.effectiveFrom}`}
          >
            {t('model_detail.unit_pricing.generation.scheduled')}
          </Chip>
        ) : null}
        {status === 'archived' ? (
          <span className="text-micro text-paper-low">
            {t('model_detail.unit_pricing.generation.archived')}
          </span>
        ) : null}
      </div>
      <Table testId={`model-unit-pricing-table-${generation.effectiveFrom}`}>
        <THead>
          <Tr interactive={false}>
            <Th>{t('model_detail.unit_pricing.column.unit')}</Th>
            <Th>{t('model_detail.unit_pricing.column.variant')}</Th>
            <Th num>{t('model_detail.unit_pricing.column.rate_micros')}</Th>
            <Th num>{t('model_detail.unit_pricing.column.rate_human')}</Th>
          </Tr>
        </THead>
        <TBody>
          {generation.rows.map((record) => (
            <Tr key={`${record.unit}-${record.variant_key}`}>
              <Td mono>{record.unit}</Td>
              <Td mono>
                {record.variant_key === ''
                  ? t('model_detail.unit_pricing.variant.default')
                  : record.variant_key}
              </Td>
              <Td num mono>{record.rate_usd_micros_per_unit}</Td>
              <Td num emphasis>{humanRate(record)}</Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

// ─── new generation form ─────────────────────────────────────────────────

interface NewGenerationFormProps extends BodyProps {
  /** The currently-effective generation, for the carry-forward seed. */
  readonly currentEffective: string | null;
  readonly currentRows: readonly ModelUnitPricingRecord[] | undefined;
}

function NewGenerationForm({
  vendor,
  model,
  tierAxis,
  lockedUnit,
  currentEffective,
  currentRows,
}: NewGenerationFormProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const create = useCreateUnitPricing();
  // A fresh row is born in the SKU's own unit when that unit is locked —
  // the disabled select below means creation is the only place it is set.
  const emptyRow: RateRowDraft =
    lockedUnit === undefined ? EMPTY_ROW : { ...EMPTY_ROW, unit: lockedUnit };
  // "From now on" by default — the same rule as the token pricing form (see
  // EffectiveFromPicker); the scheduled arm keeps this form's minute-level
  // datetime input and its next-local-midnight default.
  const [effectiveMode, setEffectiveMode] = useState<EffectiveFromMode>('immediate');
  const [effectiveFrom, setEffectiveFrom] = useState<string>(() =>
    nextLocalMidnightInputValue(),
  );
  const [rows, setRows] = useState<readonly RateRowDraft[]>([emptyRow]);
  const [error, setError] = useState<string | null>(null);
  // Carry the current generation's rows into the form, once per generation —
  // THE ANTI-DATA-LOSS HALF, unit edition (same rule as the token tier
  // editor): a new generation REPLACES the whole rate list, so starting from
  // one blank row means every reprice restates every (unit, variant, rate)
  // by hand, and a mistyped variant key does not fail loudly — it prices a
  // row no request resolves while the real one goes missing. Seeded once per
  // generation (`seededFor`), so a background refetch cannot overwrite what
  // the operator typed; a model with NO current generation starts blank
  // exactly as before.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  useEffect(() => {
    if (currentEffective === null || seededFor === currentEffective) return;
    if (currentRows === undefined || currentRows.length === 0) return;
    setRows(
      currentRows.map((record) => ({
        unit: record.unit,
        variantKey: record.variant_key,
        // Parsed back into the per-field controls where a template governs;
        // the default (`''`) row keeps every field empty, which is exactly
        // what composes back to `''` on submit.
        fields:
          tierAxis === undefined
            ? {}
            : parseVariantKey(tierAxis.template, record.variant_key) ?? {},
        rateMicros: String(record.rate_usd_micros_per_unit),
      })),
    );
    setSeededFor(currentEffective);
  }, [currentEffective, currentRows, seededFor, tierAxis]);

  function updateRow(index: number, patch: Partial<RateRowDraft>): void {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeRow(index: number): void {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  /**
   * The same rules the platform write face enforces, refused here first —
   * ceiling and variant charset imported from the same contracts constants
   * it validates with: 1..MAX_UNIT_PRICING_RATES rows, closed unit set,
   * `''`-or-UNIT_VARIANT_KEY_RE variant, positive safe-integer micros
   * (≤ 2^53−1 by `Number.isSafeInteger`), unique (unit, variant_key).
   */
  function validate(): CreateUnitPricingRequest | null {
    let iso: string;
    if (effectiveMode === 'immediate') {
      // The current instant, in the same millisecond-free ISO spelling the
      // scheduled arm produces.
      iso = immediateEffectiveFromIso();
    } else {
      iso = localDateTimeInputValueToIso(effectiveFrom.trim());
      if (iso.length === 0) {
        setError(t('model_detail.unit_pricing.error.effective_from_invalid'));
        return null;
      }
    }
    if (rows.length === 0) {
      setError(t('model_detail.unit_pricing.error.rows_required'));
      return null;
    }
    if (rows.length > MAX_UNIT_PRICING_RATES) {
      // Unreachable through the control — the add-row button disables at
      // the ceiling. Kept as the backstop mirroring the platform rule.
      setError(
        t('model_detail.unit_pricing.error.too_many_rows', { max: MAX_UNIT_PRICING_RATES }),
      );
      return null;
    }
    const rates: CreateUnitPricingRequest['rates'] = [];
    const seen = new Set<string>();
    for (const [index, row] of rows.entries()) {
      const line = index + 1;
      if (!isUnitUsageUnit(row.unit)) {
        setError(t('model_detail.unit_pricing.error.unit_invalid', { row: line }));
        return null;
      }
      // With a readable template the key is RECOMPOSED from the per-field
      // values through the contracts' own rule (all fields empty stays the
      // default `''` row); a half-filled or out-of-alphabet row refuses with
      // the row named rather than silently landing on the default row.
      // Without one, trimmed BEFORE the charset test, and the trimmed value
      // is what gets submitted: surrounding whitespace is operator noise,
      // never a distinct variant. The charset admits no whitespace, so
      // trimming can only turn an invalid draft valid, never launder an
      // invalid variant.
      let variantKey: string;
      if (tierAxis === undefined) {
        variantKey = row.variantKey.trim();
      } else if (allVariantValuesEmpty(tierAxis.template, row.fields)) {
        variantKey = '';
      } else {
        const composed = composeVariantKey(tierAxis.template, row.fields);
        if (composed === null) {
          setError(t('model_detail.unit_pricing.error.variant_fields_invalid', { row: line }));
          return null;
        }
        variantKey = composed;
      }
      if (variantKey !== '' && !UNIT_VARIANT_KEY_RE.test(variantKey)) {
        setError(t('model_detail.unit_pricing.error.variant_invalid', { row: line }));
        return null;
      }
      const rate = Number(row.rateMicros.trim());
      if (row.rateMicros.trim().length === 0 || !Number.isSafeInteger(rate) || rate <= 0) {
        setError(t('model_detail.unit_pricing.error.rate_invalid', { row: line }));
        return null;
      }
      const key = `${row.unit}\u0000${variantKey}`;
      if (seen.has(key)) {
        setError(
          t('model_detail.unit_pricing.error.duplicate_row', {
            unit: row.unit,
            variant: variantKey === '' ? t('model_detail.unit_pricing.variant.default') : variantKey,
          }),
        );
        return null;
      }
      seen.add(key);
      rates.push({ unit: row.unit, variant_key: variantKey, rate_usd_micros_per_unit: rate });
    }
    return { effective_from: iso, rates };
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const body = validate();
    if (body === null) return;
    try {
      await create.mutateAsync({ vendor, model, body });
      setEffectiveMode('immediate');
      setEffectiveFrom(nextLocalMidnightInputValue());
      setRows([emptyRow]);
    } catch (err) {
      // The unit rated-anchor refusal is translated exactly as the token
      // form translates its own: the latest billed instant in local time
      // plus the way out, never a raw code — same helper, same sentences.
      const anchorLatestIso = ratedAnchorConflictLatestIso(err);
      const message =
        anchorLatestIso !== null
          ? anchorLatestIso.length > 0
            ? t('model_detail.pricing.error.rated_anchor_conflict', {
                latest: formatLocalTimestamp(anchorLatestIso, { seconds: true }),
              })
            : t('model_detail.pricing.error.rated_anchor_conflict_generic')
          : err instanceof OpsApiError
            ? `${err.code}: ${err.message}`
            : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <>
      <form className="flex flex-col gap-3" onSubmit={handleSubmit} noValidate>
        <span className="text-label text-paper-mid">
          {t('model_detail.unit_pricing.add.title')}
        </span>
        {tierAxis !== undefined ? (
          <MeteringShapeSummary
            profile={tierAxis.profile}
            template={tierAxis.template}
            testId="model-unit-pricing-shape"
          />
        ) : null}
        <EffectiveFromPicker
          label={t('model_detail.unit_pricing.add.effective_from.label')}
          mode={effectiveMode}
          onModeChange={setEffectiveMode}
          scheduledValue={effectiveFrom}
          onScheduledValueChange={setEffectiveFrom}
          inputType="datetime-local"
          disabled={create.isPending}
          inputId="model-unit-pricing-effective-from"
          className="sm:max-w-xs"
        />
        {rows.map((row, index) => (
          <div
            // Index keys are safe here: rows are only appended/removed via
            // explicit operator action and carry no identity of their own.
            key={index}
            className="flex flex-col gap-2"
            data-testid={`model-unit-pricing-row-${index}`}
          >
            <div className="flex flex-wrap items-end gap-3">
              <FormField
                label={t('model_detail.unit_pricing.add.row.unit.label')}
                fieldId={`model-unit-pricing-row-${index}-unit`}
                className="w-40"
              >
                <Select
                  value={row.unit}
                  onValueChange={(next) => {
                    if (isUnitUsageUnit(next)) updateRow(index, { unit: next });
                  }}
                  options={KNOWN_UNIT_USAGE_UNITS.map((unit) => ({ value: unit, label: unit }))}
                  mono
                  // The unit is not a choice when the governing spec declares
                  // it: rating bills in the spec's unit, so any other option
                  // here is a row no request would ever match. Locked, not
                  // hidden, so the operator still SEES what the SKU sells in.
                  disabled={create.isPending || lockedUnit !== undefined}
                  ariaLabel={t('model_detail.unit_pricing.add.row.unit.label')}
                  testId={`model-unit-pricing-row-${index}-unit`}
                />
              </FormField>
              {tierAxis === undefined ? (
                <FormField
                  label={t('model_detail.unit_pricing.add.row.variant.label')}
                  fieldId={`model-unit-pricing-row-${index}-variant`}
                  className="min-w-[10rem] flex-1"
                >
                  <Input
                    id={`model-unit-pricing-row-${index}-variant`}
                    type="text"
                    mono
                    value={row.variantKey}
                    onChange={(e) => updateRow(index, { variantKey: e.currentTarget.value })}
                    data-testid={`model-unit-pricing-row-${index}-variant`}
                    disabled={create.isPending}
                    placeholder={t('model_detail.unit_pricing.add.row.variant.placeholder')}
                  />
                </FormField>
              ) : (
                <VariantFieldInputs
                  template={tierAxis.template}
                  values={row.fields}
                  onChange={(values) => updateRow(index, { fields: values })}
                  disabled={create.isPending}
                  idPrefix={`model-unit-pricing-row-${index}`}
                />
              )}
              <FormField
                label={t('model_detail.unit_pricing.add.row.rate.label')}
                fieldId={`model-unit-pricing-row-${index}-rate`}
                className="min-w-[10rem] flex-1"
              >
                <Input
                  id={`model-unit-pricing-row-${index}-rate`}
                  type="number"
                  mono
                  min="1"
                  step="1"
                  value={row.rateMicros}
                  onChange={(e) => updateRow(index, { rateMicros: e.currentTarget.value })}
                  data-testid={`model-unit-pricing-row-${index}-rate`}
                  disabled={create.isPending}
                  placeholder={t('model_detail.unit_pricing.add.row.rate.placeholder')}
                />
              </FormField>
              <button
                type="button"
                onClick={() => removeRow(index)}
                disabled={create.isPending || rows.length === 1}
                aria-label={t('model_detail.unit_pricing.add.row.remove_aria', { row: index + 1 })}
                data-testid={`model-unit-pricing-row-${index}-remove`}
                className="mb-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-paper-low transition-colors duration-120 hover:text-bad focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:opacity-40"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            </div>
            {tierAxis !== undefined ? (
              <VariantKeyPreview
                template={tierAxis.template}
                values={row.fields}
                testId={`model-unit-pricing-row-${index}-key-preview`}
                emptyMeansDefault
              />
            ) : null}
          </div>
        ))}
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setRows((prev) => [...prev, emptyRow])}
            disabled={create.isPending || rows.length >= MAX_UNIT_PRICING_RATES}
            data-testid="model-unit-pricing-add-row"
          >
            {t('model_detail.unit_pricing.add.add_row')}
          </Button>
          <span className="text-micro text-paper-low">
            {t('model_detail.unit_pricing.add.row_count', {
              count: rows.length,
              max: MAX_UNIT_PRICING_RATES,
            })}
          </span>
          <div className="flex-1" />
          <Button
            type="submit"
            variant="primary"
            loading={create.isPending}
            data-testid="model-unit-pricing-submit"
          >
            {create.isPending
              ? t('model_detail.unit_pricing.add.submitting')
              : t('model_detail.unit_pricing.add.submit')}
          </Button>
        </div>
      </form>
      {error !== null ? (
        <p
          className="m-0 text-label text-bad"
          role="alert"
          data-testid="model-unit-pricing-error"
        >
          {error}
        </p>
      ) : null}
    </>
  );
}
